import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { AsH2, CaptionSM, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '../GuildSignupContext'

export const GuildPredictsWinnersSection: React.FC = () => {
  const { t } = useTranslate('join-the-guild')
  const { guildPredictsWinners } = useGuildSignupContext()

  if (!guildPredictsWinners) return null

  const yearSpan = guildPredictsWinners?.customData[0]?.yearSpan ?? '2024-2025'
  const updatedDate = guildPredictsWinners?.customData[0]?.updatedDate ?? 'January 6, 2025'

  const sortedCompanies = [...(guildPredictsWinners?.customData[0]?.companies ?? [])].sort(
    (a: StatsCompanyReference, b: StatsCompanyReference) => a.score - b.score,
  )

  return (
    <section
      className={classNames(
        'flex flex-col items-center justify-center py-16 md:py-28 text-core-gray-950 bg-core-gray-100 w-full',
      )}
      id="guild-collection"
      aria-labelledby="guild-predicts-winners-title"
    >
      <div className="mx-8 flex w-fit flex-col items-start gap-1 md:mx-4 md:flex-row md:gap-8">
        <div>
          <Image
            src={getCloudinaryImageUrl({
              path: '/v1700075329/angel-studios/guild/Version_Guild_Angel_Copper_OnLight_Yes.webp',
              width: 90,
              height: 112,
            })}
            width={90}
            height={112}
            alt={t('angelGuildAlt', 'Angel Guild')}
            loading="lazy"
            className="mx-auto mb-4 w-[60px] md:w-[70px] lg:w-[82px]"
          />
        </div>
        <div className="flex flex-col gap-y-2">
          <HeadingXS
            as={AsH2}
            weight="bold"
            id="guild-predicts-winners-title"
            className="md:photon-heading-sm lg:photon-heading-md"
          >
            {t('guildPredictsWinners', 'Angel Guild Predicts Winners')}
          </HeadingXS>
          <ParagraphSM color={'core-gray-800'} className="md:photon-paragraph-md max-w-[400px]">
            {t(
              'averageRottenTomatoesPopcornmeter',
              'Average Rotten Tomatoes Popcornmeter for wide releases by studio for {{ yearSpan }}.',
              { yearSpan },
            )}
          </ParagraphSM>
        </div>
      </div>
      <When condition={sortedCompanies?.length > 0}>
        <>
          <div className="mt-8 flex flex-row gap-2 border-b border-solid border-core-gray-400 px-4 md:gap-4 lg:gap-6">
            {sortedCompanies.map((item: StatsCompanyReference, index: React.Key) => (
              <div key={index} className="flex flex-col items-center justify-end">
                <Image
                  src={
                    item.imageUrl.includes('.svg')
                      ? item.imageUrl
                      : getCloudinaryImageUrl({
                          path: item.imageUrl,
                          width: 50,
                        })
                  }
                  width={50}
                  height={50}
                  alt={item.name}
                  loading="lazy"
                  className="mb-2 max-w-[45px] md:max-w-[50px] se:max-w-[40px]"
                />
                <div
                  className={classNames('w-10 md:w-12 lg:w-14 bg-core-gray-950 flex justify-center pt-1', {
                    'bg-oxide': item.name === 'Angel',
                  })}
                  style={{ height: `${item.score * 1.5}px` }}
                >
                  <CaptionSM weight="semibold" color="white">{`${item.score}%`}</CaptionSM>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-row items-center justify-center gap-2 md:gap-4">
            <Image
              width={65}
              height={19}
              src={getCloudinaryImageUrl({
                path: '/co_rgb:565453,e_colorize:100/v1741120453/angel-studios/guild/predicts-winners/image_37.webp',
                width: 65,
                height: 19,
              })}
              alt={'Rotten Tomatoes Logo'}
              loading="lazy"
              className="mb-2"
            />
            <CaptionSM>{t('asOfDate', 'As of {{ updatedDate }}', { updatedDate })}</CaptionSM>
          </div>
        </>
      </When>
    </section>
  )
}

interface StatsCompanyReference {
  imageUrl: string
  name: string
  score: number
}
